import { FC, useState } from 'react';
import { InputContainer } from '../InputContainer';
import { InputProps, withField } from '../withField';
import { getDecimalPlaces } from './helpers/number';

export interface DecimalInputProps {
  placeholder?: string;

  suffix?: string;
  prefix?: string;

  min?: number;
  max?: number;
  readOnly?: boolean;
  maxDp?: number;
}

export const DecimalInput: FC<InputProps<number | string> & DecimalInputProps> = ({
  onChange,
  value = 0,
  id,
  placeholder,
  suffix,
  prefix,
  min,
  max,
  readonly,
  maxDp,
  autofocus
}) => {
  const [currentText, setCurrentText] = useState<string>(value.toString());

  const [step, setStep] = useState(1);

  const onTextChange = (text: string) => {
    if (!text) {
      onChange(0);
      setCurrentText('0');

      return;
    }

    const textToSet = text;
    const number = parseFloat(textToSet);

    if (isNaN(number)) {
      return;
    }

    const decimalPlaces = getDecimalPlaces(textToSet);
    if (maxDp && decimalPlaces > maxDp) return;

    if (min !== undefined && number < min) {
      onChange(min);
      setCurrentText(min.toString());
      return;
    }

    if (max !== undefined && number > max) {
      onChange(max);
      setCurrentText(max.toString());
      return;
    }

    const step = decimalPlaces > 0 ? 1 / Math.pow(10, decimalPlaces) : 1;
    // HACK - If attemping to enter a decimal value that ends with 0, need to adjust the step to allow for the 0 to not be wiped out.
    if (textToSet.endsWith('0')) {
      setStep(step);
      setCurrentText(textToSet);
      onChange(textToSet);
    } else {
      setStep(step);
      setCurrentText(number.toString());
      onChange(number.toString());
    }
  };

  return (
    <InputContainer className="px-3 py-2">
      {prefix && <span className="text-secondary-std mr-0.5 text-sm">{prefix}</span>}
      <input
        autoFocus={autofocus}
        value={currentText}
        onChange={e => onTextChange(e.target.value)}
        type="number"
        step={step}
        // Ignore any e presses- target.value becomes empty because it thinks you're part way through
        // giving a floating point number.
        onKeyDown={evt => ['e', 'E'].includes(evt.key) && evt.preventDefault()}
        id={id}
        className="placeholder:text-secondary-std block w-full flex-1 border-0 bg-transparent p-0 text-sm placeholder:opacity-60 focus:ring-0 sm:leading-6"
        placeholder={placeholder}
        readOnly={readonly}
      />
      {suffix && <span className="text-secondary-std ml-0.5 text-sm">{suffix}</span>}
    </InputContainer>
  );
};

export const DecimalField = withField<number | string, DecimalInputProps>(DecimalInput);
